import { z } from "zod";

/**
 * Wire-shape schema for `user.getCDNKey`. Single source of truth — the trpc
 * router imports this for `.output(...)` and the client durable-cache layer
 * (`apps/mobile/hooks/trpc/useCDNKey.ts`) imports it as the parse target.
 *
 * Dates round-trip through trpc's superjson transformer as `Date` instances,
 * which is what callers like `signUrlSyncIfPossible` (in
 * `apps/mobile/utils/index.ts`) feed straight into `dayjs(key.expiry)` —
 * `dayjs` accepts `Date | string`, but the persisted form here matches
 * what trpc actually delivers.
 *
 * `findFirst` in the underlying drizzle query can produce `undefined` when
 * the cdn_keys table is empty; the router normalizes to `null` so the wire
 * shape is `T | null` rather than `T | undefined` (zod nullable).
 */
// `timestampWithTimezone` in drizzle-pg returns `string` (not `Date`) by
// default — that's what the trpc-superjson transformer ultimately ships
// over the wire. The client durable-cache parse target therefore uses
// `z.string()` rather than `z.date()`.
const cdnKeyShape = z.object({
  id: z.string(),
  expiry: z.string(),
  // Wide directory token for the legacy `/audio/` tree.
  bunnyCDNPath: z.string().nullable(),
  // Wide directory token for the on-demand `/songs/` tree. `nullish` (not
  // just nullable): the server always sends it, but durable-cache entries
  // persisted by an older client build predate the field entirely — tolerate
  // its absence so stale offline data still parses. Clients degrade to
  // unsigned `/songs/` URLs whenever it's missing/null.
  bunnyCDNPathSongs: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const cdnKeyWireSchema = cdnKeyShape.nullable();

export type CDNKey = z.infer<typeof cdnKeyWireSchema>;
